import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, CreditCard, Printer, Check } from "lucide-react";
import { toast } from "sonner";

const CARDC = { active: "bg-emerald-100 text-emerald-700", suspended: "bg-amber-100 text-amber-700" };

export default function CardsDirectory() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [backend] = useState(process.env.REACT_APP_BACKEND_URL);
  const load = () => api.get("/people/cards").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const markPrinted = async (id) => { try { await api.patch(`/people/cards/${id}/mark-printed`); toast.success(t("markPrinted")); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const filtered = rows.filter((c) => !q || (c.student_name || "").toLowerCase().includes(q.toLowerCase()) || (c.card_uid || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="cards-directory">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-display font-bold text-slate-900">{t("studentCards")}</h1>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="cards-search" className="pl-9 w-56" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>
      <Card className="card-float border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead>{t("name")}</TableHead><TableHead>Code</TableHead><TableHead>{t("cardStatus")}</TableHead>
            <TableHead>{t("printed")}</TableHead><TableHead className="text-right">{t("actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((c) => (
              <TableRow key={c.id} data-testid="card-dir-row">
                <TableCell className="font-medium text-slate-800"><Link className="hover:underline" to={`/people/students/${c.student_id}`}>{c.student_name}</Link></TableCell>
                <TableCell className="font-mono text-xs text-slate-600">{c.card_uid}</TableCell>
                <TableCell><Badge className={`${CARDC[c.status] || "bg-slate-100"} border-0`}>{c.status}</Badge></TableCell>
                <TableCell>
                  {c.printed_at
                    ? <Badge className="bg-sky-100 text-sky-700 border-0">{t("printed")} ({c.print_count})</Badge>
                    : <Badge className="bg-slate-100 text-slate-400 border-0">{t("notPrinted")}</Badge>}
                </TableCell>
                <TableCell className="text-right space-x-2">
                  <a href={`${backend}/api/people/cards/${c.id}/pdf`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="gap-1" data-testid={`reprint-${c.id}`}><Printer className="h-3.5 w-3.5" />PDF</Button>
                  </a>
                  <Button size="sm" variant="secondary" className="gap-1" data-testid={`markprinted-${c.id}`} onClick={() => markPrinted(c.id)}><Check className="h-3.5 w-3.5" />{c.printed_at ? t("reprint") : t("markPrinted")}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
